<script setup lang="ts">
import {ref} from 'vue';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';
import {useMoods} from '@/ts/composables/pure/use-moods';
import MoodFace from '@/vue/atoms/mood-face.vue';

/**
 * The ten moods in a single row, for the journal's entry modal (IPD-3107).
 *
 * The Today tile asks the same question with the same faces, but lays them out as a grid inside a
 * three-step wizard; here everything sits on one screen, so the row is its own component rather
 * than a shared one. Picking the selected mood again clears it — a mood is optional, and there is
 * no other way to take it back.
 */
const selected = defineModel<MoodDTO | null>({default: null});

const {moods} = useMoods();

/** Which option carries the group's single tab stop; see `onKeydown`. */
const focused = ref(0);
const optionButtons = ref<HTMLButtonElement[]>([]);

/** Both axes move the selection: the moods read as one list however they happen to wrap. */
const ARROW_STEP: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

function select(mood: MoodDTO, index: number): void {
  selected.value = selected.value === mood ? null : mood;
  focused.value = index;
}

/**
 * A radiogroup is one stop in the tab order, not ten: the arrows move between the options and Tab
 * leaves the group. Mirrors the Today tile's picker, which asks the same question with the same
 * faces — declaring the role without this would promise assistive tech a behaviour we do not have.
 */
function onKeydown(event: KeyboardEvent, index: number): void {
  const offset = ARROW_STEP[event.key] ?? 0;
  if (offset === 0) {
    return;
  }
  event.preventDefault();
  // Wraps, as the pattern expects, so the ends of the list are not dead stops.
  const next = (index + offset + moods.length) % moods.length;
  focused.value = next;
  // Moving focus selects, which is single-select radiogroup behaviour: what the user hears as
  // they arrow through is what they would save.
  selected.value = moods[next].key;
  optionButtons.value[next]?.focus();
}
</script>

<template>
  <div class="mood-selector" role="radiogroup" :aria-label="$t('journal_tile_mood_question')">
    <button
      v-for="(mood, index) in moods"
      :key="mood.key"
      ref="optionButtons"
      type="button"
      class="ms-option"
      role="radio"
      :aria-checked="selected === mood.key"
      :tabindex="index === focused ? 0 : -1"
      :class="{'ms-option--selected': selected === mood.key}"
      @click="select(mood.key, index)"
      @keydown="onKeydown($event, index)"
    >
      <mood-face :mood="mood.key" :size="44" tinted/>
      <span class="ms-label">{{ $t(`mood_${mood.key}`) }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.mood-selector {
  // A grid, not a flex row: the dialog sizes itself to its content, so a row would push the modal
  // wider and clip the last moods instead of wrapping. Fixed tracks make the ten fit the width
  // they are given, however narrow — a mood that scrolls out of reach cannot be picked.
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--Spacing-spacing-2);
  width: 100%;

  @include screen-md {
    grid-template-columns: repeat(10, 1fr);
    gap: var(--Spacing-spacing-1);
  }
}

.ms-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-2);
  background: none;
  border: none;
  padding: var(--Spacing-spacing-2) var(--Spacing-spacing-1);
  border-radius: var(--card-border-radius);
  cursor: pointer;
  min-width: 0;
  @include focus-visible;

  // Only the face dims while unpicked. Fading the whole button would take the label with it and
  // drop its contrast below the accessible minimum — the names have to stay readable to be read.
  :deep(.mood-face) {
    opacity: 0.55;
    transition: opacity 0.15s ease;
  }

  &:hover :deep(.mood-face),
  &--selected :deep(.mood-face) {
    opacity: 1;
  }

  &--selected {
    background: var(--Neutral-UI-neutral-ui-10);
  }
}

.ms-label {
  @include font-metric-sm;
  color: var(--Neutral-UI-neutral-ui-80);
  text-align: center;
}
</style>
